import { useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { disablePush, enablePush, isSubscribed, needsInstallFirst, permission, pushSupported } from '../push'

export default function NotificationSettings() {
  const { push, baby } = usePage().props
  const vapidPublicKey = push?.vapidPublicKey

  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [checked, setChecked] = useState(false)
  const [testResult, setTestResult] = useState(null)

  useEffect(() => {
    isSubscribed()
      .then(setSubscribed)
      .catch(() => setSubscribed(false))
      .finally(() => setChecked(true))
  }, [])

  // Nothing to configure when the server has no VAPID keys — say so rather than showing a
  // switch that can only fail.
  if (!vapidPublicKey) {
    return (
      <Card>
        <p className="text-ink-soft">
          Notifications aren't switched on for this album yet.
        </p>
      </Card>
    )
  }

  if (needsInstallFirst()) {
    return (
      <Card>
        <p className="text-ink-soft">
          iPhones only allow notifications from an installed app. Tap <strong>Share</strong> in
          Safari, then <strong>Add to Home Screen</strong>, and open {baby?.name || 'the album'} from
          there — this switch appears once you do.
        </p>
      </Card>
    )
  }

  if (!pushSupported()) {
    return (
      <Card>
        <p className="text-ink-soft">This browser can't do notifications. On iPhone you'll need iOS 16.4 or newer.</p>
      </Card>
    )
  }

  const blocked = permission() === 'denied'

  async function toggle() {
    setBusy(true)
    setError(null)

    try {
      if (subscribed) {
        await disablePush()
        setSubscribed(false)
      } else {
        const granted = await enablePush(vapidPublicKey)
        setSubscribed(granted)
        if (!granted) setError("Your device said no to notifications. You can change that in Settings.")
      }
    } catch (failure) {
      setError(failure.message)
    } finally {
      setBusy(false)
    }
  }

  // New-photo alerts skip whoever uploaded, so this is the only way to check your own device.
  async function sendTest() {
    setError(null)
    setTestResult('Sending…')

    try {
      const response = await fetch('/push_subscription/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        },
      })

      if (!response.ok) throw new Error(`Server said ${response.status}`)

      const { sent, reason } = await response.json()

      if (reason === 'no_subscriptions') {
        setTestResult('This device isn\u2019t registered yet — turn notifications on first.')
      } else if (sent > 0) {
        setTestResult('Sent! It should arrive in a moment.')
      } else {
        setTestResult('Couldn\u2019t reach this device. Try turning notifications off and on again.')
      }
    } catch (failure) {
      setError(`Test failed: ${failure.message}`)
      setTestResult(null)
    }
  }

  return (
    <Card>
      {blocked ? (
        <p className="text-ink-soft">
          Notifications are blocked for this app. Turn them back on in your device settings, then come back.
        </p>
      ) : (
        <>
          <p className="text-ink-soft">
            {subscribed
              ? "You'll get a nudge on this device whenever a new photo or video is added."
              : 'Get a nudge on this device whenever someone adds a new photo or video.'}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={toggle}
              disabled={busy || !checked}
              className={`btn-cute ${
                subscribed
                  ? 'border-2 border-blush-100 bg-white text-ink-soft'
                  : 'bg-blush-400 text-white shadow hover:bg-blush-500'
              }`}
            >
              {busy ? 'One sec…' : subscribed ? 'Turn notifications off' : '🔔 Turn notifications on'}
            </button>

            {subscribed && (
              <button
                type="button"
                onClick={sendTest}
                disabled={busy}
                className="btn-cute border-2 border-blush-100 bg-white text-ink-soft hover:bg-blush-50"
              >
                Send a test
              </button>
            )}
          </div>

          {testResult && <p className="mt-3 text-sm font-bold text-ink-soft">{testResult}</p>}
          {error && <p className="mt-3 text-sm font-bold text-rose-600">{error}</p>}
        </>
      )}
    </Card>
  )
}

function Card({ children }) {
  return (
    <section className="card-cute space-y-1 p-6">
      <h2 className="font-display text-2xl font-extrabold text-ink">New photo alerts 🔔</h2>
      {children}
    </section>
  )
}
